import Icon from "../components/Icon";
import { PageTitle } from "../components";
import { useState } from "react";
import { apiFetch } from "../api";
import { recordUiError } from "../observability";

// ─── Disparo Manual (emergência quando WhatsApp/Evolution cai) ───────────────
// O operador digita o ID da rota + índice do grupo, clica em "Preparar disparo
// manual", e a página mostra os deep links wa.me (https://wa.me/<num>?text=<msg>)
// prontos pra abrir no WhatsApp Web do navegador e enviar manualmente, um por um.
//
// Fluxo:
//  1. Motorista: clica em "Perguntar origem no WhatsApp" (abre wa.me com a
//     pergunta 1/2). O motorista responde no WhatsApp do operador. O operador
//     clica em [Base] ou [Residência] aqui → chama /resposta-motorista.
//  2. Motorista: clica em "Enviar link de assinatura" (abre wa.me com o link).
//  3. Funcionários: clica em "Enviar via WhatsApp" em cada um (abre wa.me com
//     a mensagem + link de assinatura já preenchidos).
//
// É fallback de emergência: zero infra, zero custo, só código. Não substitui o
// disparo automático — é a ponte pra operar enquanto o canal oficial não sobe.

const Card = ({ children, style }) => (
  <div style={{
    background: "var(--s3)", border: "1px solid var(--border)", borderRadius: 12,
    padding: 16, marginBottom: 12, color: "var(--text)", ...style,
  }}>{children}</div>
);

const WaButton = ({ href, label, disabled }) => {
  if (disabled || !href) {
    return <button disabled style={btnStyle(true)}>{label}</button>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={btnStyle(false)}>
      {label}
    </a>
  );
};

const btnStyle = (disabled) => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 14px", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
  background: disabled ? "var(--s2)" : "#25D366", color: disabled ? "var(--muted)" : "#fff",
  fontWeight: 600, fontSize: '0.9375rem', textDecoration: "none",
  border: "1px solid rgba(0,0,0,0.1)", opacity: disabled ? 0.5 : 1,
});

function DisparoResultado({ resultado, onConfirmar }) {
  const [respostaStatus, setRespostaStatus] = useState({});
  const [respostaManual, setRespostaManual] = useState(resultado.respostaManualInicial || null);

  if (resultado.erro && !resultado.preview) {
    return (
      <Card style={{ background: "rgba(220,38,38,0.08)", borderColor: "rgba(220,38,38,0.3)" }}>
        <strong style={{ color: "#dc2626" }}>Pedido {resultado.pedido} — erro</strong>
        <div style={{ color: "#dc2626", fontSize: '0.9375rem', marginTop: 4 }}>{resultado.erro}</div>
      </Card>
    );
  }

  const preview = resultado.preview;
  const p = resultado.payload;

  const registrarRespostaMotorista = async (resposta) => {
    const id = p.rota_id;
    const tel = p.motorista?.telefone;
    setRespostaStatus({ loading: true });
    try {
      const data = await apiFetch(`/rotas/${id}/grupos/${p.grupo_index}/resposta-motorista-manual`, {
        method: "POST",
        body: JSON.stringify({ motorista_telefone: tel, resposta }),
      });
      setRespostaManual(data);
      setRespostaStatus({ ok: `Resposta '${resposta}' registrada e rota recalculada.` });
    } catch (e) {
      setRespostaStatus({ erro: e?.message || "Erro ao registrar resposta do motorista." });
    }
  };

  // ── Estado 1: só preview (busca, read-only). Mostra a rota e um botão pra confirmar. ──
  if (!p) {
    return (
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <strong style={{ color: "var(--text)" }}>Pedido {resultado.pedido} → Rota {preview.rota_id}</strong> · {preview.group_label} ·
            {" "}{preview.trecho} {preview.horario} {preview.data} · {preview.funcionarios.length} passageiro(s)
            <div style={{ marginTop: 8, color: "var(--muted)", fontSize: '0.875rem' }}>
              <Icon name="person" /> Motorista: {preview.motorista?.nome || "—"} ({preview.motorista?.telefone || "sem telefone"})
            </div>
            <div style={{ marginTop: 6, color: "var(--muted)", fontSize: '0.875rem' }}>
              <Icon name="people" /> {preview.funcionarios.map((f) => f.nome || "—").join(", ") || "nenhum passageiro"}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <button onClick={onConfirmar} disabled={resultado.disparando} style={primaryBtn(resultado.disparando)}>
              {resultado.disparando ? "Preparando..." : <><Icon name="send" size={16} /> Preparar comunicação</>}
            </button>
            {resultado.erro && <div style={{ ...erroStyle, marginTop: 8 }}>{resultado.erro}</div>}
          </div>
        </div>
        <div style={{ marginTop: 10, padding: 10, background: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 8, fontSize: '0.875rem', color: "var(--muted)" }}>
          Preparar gera os links, mas <strong>não registra envio</strong> e não marca as assinaturas como enviadas.
        </div>
      </Card>
    );
  }

  // ── Estado 2: disparo confirmado. Mostra os links wa.me. ──
  const motorista = respostaManual?.motorista || p.motorista;
  return (
    <div>
      <Card style={{ background: "rgba(37,211,102,0.08)", borderColor: "rgba(37,211,102,0.3)" }}>
        <strong style={{ color: "var(--text)" }}>Pedido {resultado.pedido} → Rota {p.rota_id}</strong> · {p.group_label} ·
        {" "}{p.trecho} {p.horario} {p.data} · {p.funcionarios.length} passageiro(s) · <span style={{ color: "#60a5fa" }}>✓ preparado</span>
      </Card>

      <Card>
        <h3 style={{ ...h3Style, color: "var(--text)" }}><Icon name="person" /> Motorista: {p.motorista?.nome || "—"}</h3>
        <div style={{ color: "var(--muted)", fontSize: '0.875rem', marginBottom: 10 }}>
          Telefone: {p.motorista?.telefone || "—"}
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={stepLabel}>Passo 1 — perguntar de onde ele sai:</div>
          <WaButton href={p.motorista?.pergunta_wa_me} label="💬 Perguntar origem no WhatsApp" />
          <span style={hintStyle}>abre o WhatsApp com a pergunta 1/2 pronta</span>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={stepLabel}>Passo 2 — registrar a resposta que ele der (recalcula a rota e gera o mapa):</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => registrarRespostaMotorista("1")} style={respostaBtn} disabled={respostaStatus.loading}>1 — Base</button>
            <button onClick={() => registrarRespostaMotorista("2")} style={respostaBtn} disabled={respostaStatus.loading}>2 — Residência</button>
            {respostaStatus.loading && <span style={hintStyle}>recalculando rota...</span>}
            {respostaStatus.ok && <span style={{ ...hintStyle, color: "#25D366" }}>✓ {respostaStatus.ok}</span>}
            {respostaStatus.erro && <span style={{ ...hintStyle, color: "#dc2626" }}>✗ {respostaStatus.erro}</span>}
          </div>
        </div>

        {!respostaManual && (
          <div style={{ padding: 10, background: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 8, fontSize: '0.875rem', color: "var(--muted)" }}>
            ⏳ <strong>Registre a origem (Passo 2)</strong> pra liberar o link do mapa e as assinaturas.
            O mapa depende de onde o motorista sai.
          </div>
        )}

        {respostaManual && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <div style={stepLabel}>Passo 3 — enviar o link do mapa da rota pro motorista:</div>
              <WaButton href={motorista?.maps_wa_me} label="🗺️ Enviar link do mapa" />
              {!motorista?.maps_link && <span style={{ ...hintStyle, color: "#dc2626" }}>mapa não gerado</span>}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={stepLabel}>Passo 4 — enviar a assinatura de INÍCIO pro motorista:</div>
              <WaButton href={motorista?.assinatura_inicio_wa_me} label="✍️ Enviar assinatura (início)" />
            </div>

            <div>
              <div style={stepLabel}>Passo 5 — enviar a assinatura de FIM (KM final) pro motorista:</div>
              <WaButton href={motorista?.assinatura_fim_wa_me} label="🏁 Enviar assinatura (fim)" />
            </div>
          </div>
        )}
      </Card>

      {respostaManual && (
        <Card>
          <h3 style={{ ...h3Style, color: "var(--text)" }}><Icon name="people" /> Passageiros ({p.funcionarios.length})</h3>
          <p style={{ ...hintStyle, marginBottom: 10 }}>
            Clique em cada "Enviar via WhatsApp" — abre o WhatsApp Web com a mensagem
            e o link de assinatura já preenchidos. Só aperte enviar.
          </p>
          {p.funcionarios.map((f, i) => (
            <div key={f.id || i} style={{
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              padding: "10px 0", borderTop: i > 0 ? "1px solid var(--border)" : "none",
            }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 600, fontSize: '1rem', color: "var(--text)" }}>{f.nome || "—"}</div>
                <div style={{ color: "var(--muted)", fontSize: '0.875rem' }}>{f.telefone || "sem telefone"}</div>
              </div>
              <WaButton href={f.wa_me} label="💬 Enviar via WhatsApp" disabled={!f.wa_me} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

export default function DisparoManual() {
  const [rotasInput, setRotasInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [resultados, setResultados] = useState([]);
  const showError = (message) => {
    recordUiError({ message, page: "disparo_manual_legacy", source: "inline_error" });
    setErro(message);
  };

  const parsePedidos = (input) => {
    if (!input || !input.trim()) return [];
    return input.split(/[,\n;\s]+/).map((s) => s.trim()).filter(Boolean);
  };

  const buscar = async () => {
    setErro(null); setResultados([]);
    const pedidos = parsePedidos(rotasInput);
    if (!pedidos.length) { showError("Informe ao menos um pedido. Ex: 2026/53834 ou 2026/53834, 2026/53835"); return; }
    setLoading(true);
    try {
      const data = await apiFetch(`/disparo-manual/buscar-por-pedido`, {
        method: "POST",
        body: JSON.stringify({ pedidos }),
      });
      const res = (data.resultados || []).map((r) => ({
        rotaId: r.rota_id,
        grupoIndex: r.grupo_index,
        pedido: r.pedido,
        preview: r.preview || null,
        payload: r.payload || null,
        respostaManualInicial: r.respostaManual || null,
        disparando: false,
        erro: r.erro || null,
      }));
      setResultados(res);
    } catch (err) {
      showError(err?.message || "Erro ao buscar pedidos.");
    }
    setLoading(false);
  };

  const confirmarDisparo = async (idx) => {
    const r = resultados[idx];
    if (!r || !r.preview || r.payload) return;
    setResultados((curr) => curr.map((x, i) => (i === idx ? { ...x, disparando: true, erro: null } : x)));
    try {
      const data = await apiFetch(`/rotas/${r.rotaId}/grupos/${r.grupoIndex}/disparar-manual`, { method: "POST" });
      setResultados((curr) => curr.map((x, i) => (i === idx ? { ...x, payload: data, disparando: false } : x)));
    } catch (err) {
      setResultados((curr) => curr.map((x, i) => (i === idx ? { ...x, disparando: false, erro: err?.message || "Erro ao confirmar disparo." } : x)));
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "0 auto", color: "var(--text)" }}>
      <PageTitle icon="warning">Disparo Manual (Emergência)</PageTitle>
      <p style={{ color: "var(--muted)", fontSize: '0.9375rem', marginBottom: 16 }}>
        Use quando o envio automático estiver indisponível. O sistema prepara os links de assinatura
        e você envia manualmente pelo WhatsApp Web do seu navegador, clicando em cada botão.
      </p>

      <Card>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>Pedidos (uma ou vários, separados por vírgula)</label>
            <input
              type="text" value={rotasInput} onChange={(e) => setRotasInput(e.target.value)}
              placeholder="ex: 2026/53834  ou  2026/53834, 2026/53835" style={inputStyle}
            />
          </div>
          <button onClick={buscar} disabled={loading} style={primaryBtn(loading)}>
            {loading ? "Buscando..." : <><Icon name="search" size={16} /> Buscar rotas</>}
          </button>
        </div>
        {erro && <div style={erroStyle}>{erro}</div>}
      </Card>

      {resultados.length > 0 && (
        <div>
          {resultados.map((r, i) => (
            <DisparoResultado
              key={`${r.rotaId}-${r.grupoIndex}-${i}`}
              resultado={r}
              index={i}
              onConfirmar={() => confirmarDisparo(i)}
            />
          ))}

          <div style={{ marginTop: 16, padding: 12, background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.3)", borderRadius: 8, fontSize: '0.875rem', color: "var(--muted)" }}>
            <Icon name="warning" /> <strong>Lembrete:</strong> não atropele — clique/envie um por vez (~15-30s cada).
            Rajada manual também pode marcar o seu número. Recusas que chegarem no seu
            WhatsApp você precisa registrar no sistema (fluxo de recusa manual — próxima versão).
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: "block", fontSize: '0.875rem', fontWeight: 600, marginBottom: 4, color: "var(--muted)" };
const inputStyle = {
  width: "100%", padding: "8px 10px", borderRadius: 8,
  border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", fontSize: '1rem',
};
const primaryBtn = (loading) => ({
  padding: "9px 16px", borderRadius: 8, cursor: loading ? "not-allowed" : "pointer",
  background: loading ? "var(--s2)" : "var(--accent)", color: loading ? "var(--text)" : "var(--on-accent)",
  fontWeight: 600, fontSize: '1rem', border: "none", opacity: loading ? 0.6 : 1,
});
const erroStyle = { marginTop: 10, padding: "8px 12px", background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8, color: "#dc2626", fontSize: '0.9375rem' };
const h3Style = { fontSize: '1rem', fontWeight: 700, marginBottom: 8 };
const stepLabel = { fontSize: '0.875rem', fontWeight: 600, color: "var(--muted)", marginBottom: 6 };
const hintStyle = { fontSize: '0.875rem', color: "var(--muted)", marginLeft: 8 };
const respostaBtn = {
  padding: "6px 12px", borderRadius: 8, cursor: "pointer",
  background: "var(--s3)", border: "1px solid var(--border)", color: "var(--text)",
  fontSize: '0.9375rem', fontWeight: 600,
};
